import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ScanLine, Layers, Store, MapPin, Star } from "lucide-react";
import { SectionHeading, GlassCard } from "./GlassCard";

const shops = [
  {
    name: "Kaushik electronics",
    category: "Electronics",
    distance: "1.8 km",
    rating: "4.8",
    products: [
      { name: "Bluetooth Speaker", price: "₹1,499", old: "₹1,999" },
      { name: "Smart LED TV 43\"", price: "₹21,999", old: "₹26,500" },
      { name: "Wireless Earbuds", price: "₹899", old: "₹1,299" },
    ],
  },
  {
    name: "Vikash jewelers",
    category: "Jewelry",
    distance: "2.3 km",
    rating: "4.9",
    products: [
      { name: "New design ear rings", price: "₹1,000", old: "₹1,200" },
      { name: "Gold-plated necklace set", price: "₹2,499", old: "₹2,999" },
      { name: "Designer bangles (pair)", price: "₹749", old: "₹950" },
    ],
  },
  {
    name: "Sharma grocery mart",
    category: "Grocery",
    distance: "0.9 km",
    rating: "4.7",
    products: [
      { name: "Basmati rice 5kg", price: "₹549", old: "₹640" },
      { name: "Cold-pressed oil 1L", price: "₹299", old: "₹360" },
      { name: "Monthly combo pack", price: "₹1,199", old: "₹1,450" },
    ],
  },
];

const pillars = [
  { icon: ScanLine, title: "Scan & list", desc: "QR se seedha catalogue — customers scan karte hi aapki dukaan khul jaati hai." },
  { icon: Layers, title: "Unlimited catalogue", desc: "Photos, prices, discounts — jitne chaaho products, koi limit nahi." },
  { icon: Store, title: "Your brand, your store", desc: "Aapka naam, aapki pehchaan. Gromza sirf launchpad hai, dukaan aapki hai." },
];

export const Storefront = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % shops.length), 4500);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="storefront" data-testid="storefront-section" className="relative py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeading
          testId="storefront-heading"
          eyebrow="Digital Storefront"
          title="Real shops."
          highlight="Live prices."
          description="A holographic window into the stores already selling on Gromza — browse live catalogues and order from the lane next door."
        />

        <div className="mt-16 relative [perspective:1400px]" data-testid="storefront-carousel">
          <div className="relative h-[430px] sm:h-[400px] flex items-center justify-center">
            <AnimatePresence initial={false}>
              {shops.map((shop, i) => {
                const pos = (i - active + shops.length) % shops.length;
                const isCenter = pos === 0;
                const xOffset = pos === 1 ? 1 : pos === shops.length - 1 ? -1 : 0;
                return (
                  <motion.div
                    key={shop.name}
                    data-testid={`carousel-card-${i}`}
                    animate={{
                      x: `${xOffset * 62}%`,
                      scale: isCenter ? 1 : 0.78,
                      rotateY: xOffset * -28,
                      opacity: isCenter ? 1 : Math.abs(xOffset) === 1 ? 0.45 : 0,
                      zIndex: isCenter ? 20 : 10,
                    }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute w-full max-w-md"
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    <div
                      className={`glass-panel rounded-3xl p-7 ${
                        isCenter ? "border-amber-400/40 shadow-[0_0_60px_rgba(251,191,36,0.15)]" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between mb-5">
                        <div>
                          <h3 className="font-display text-2xl font-semibold capitalize">{shop.name}</h3>
                          <p className="text-xs text-slate-400 mt-1">{shop.category}</p>
                        </div>
                        <div className="text-right text-xs text-slate-400 space-y-1">
                          <p className="inline-flex items-center gap-1 text-amber-300"><MapPin size={11} /> {shop.distance}</p>
                          <p className="flex items-center justify-end gap-1"><Star size={11} className="text-amber-400" fill="currentColor" /> {shop.rating}</p>
                        </div>
                      </div>
                      <div className="space-y-2.5">
                        {shop.products.map((p) => (
                          <div key={p.name} className="flex items-center justify-between rounded-xl bg-white/[0.04] border border-white/[0.07] px-4 py-3">
                            <span className="text-sm text-slate-200">{p.name}</span>
                            <span className="text-sm">
                              <span className="font-semibold text-amber-400">{p.price}</span>{" "}
                              <span className="text-xs text-slate-500 line-through ml-1">{p.old}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                      <button
                        data-testid={`view-catalog-button-${i}`}
                        className="mt-6 w-full rounded-full border border-amber-400/40 bg-amber-400/10 py-3 text-sm font-semibold text-amber-300 hover:bg-amber-400 hover:text-[#0b0f19] transition-[background-color,color] duration-300"
                      >
                        View Catalog
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              data-testid="carousel-prev-button"
              onClick={() => setActive((a) => (a - 1 + shops.length) % shops.length)}
              className="w-11 h-11 rounded-full glass-panel flex items-center justify-center text-amber-400 hover:border-amber-400/50 transition-colors"
              aria-label="Previous shop"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex gap-2" data-testid="carousel-dots">
              {shops.map((_, i) => (
                <button
                  key={i}
                  data-testid={`carousel-dot-${i}`}
                  onClick={() => setActive(i)}
                  className={`h-1.5 rounded-full transition-[width,background-color] duration-400 ${i === active ? "w-8 bg-amber-400" : "w-3 bg-white/20"}`}
                  aria-label={`Go to shop ${i + 1}`}
                />
              ))}
            </div>
            <button
              data-testid="carousel-next-button"
              onClick={() => setActive((a) => (a + 1) % shops.length)}
              className="w-11 h-11 rounded-full glass-panel flex items-center justify-center text-amber-400 hover:border-amber-400/50 transition-colors"
              aria-label="Next shop"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="mt-16 grid sm:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <GlassCard key={p.title} testId={`storefront-pillar-${i}`} delay={i} className="p-8 group">
              <div className="w-12 h-12 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center mb-5 group-hover:bg-amber-400/20 transition-colors duration-400">
                <p.icon size={22} className="text-amber-400" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">{p.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </section>
  );
};
